import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import IconButton from "@mui/material/IconButton";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";

import TableWithPaginationSorting from "../../Common/Table/TableWithPaginationSorting";
import { KMKDialog } from "../../Common";

import { convertTimezone } from "../../../util/common";
import { dayjs } from "../../../util/dayjs";
import AppService from "../../../service/appService";

const useStyles = makeStyles()(() => ({
  dialogPaper: {
    "& .MuiDialog-paper": {
      maxWidth: "960px",
      width: "960px",
    },
  },
  contentContainer: {
    width: "100%",
    textAlign: "left",
    minHeight: "100px",
    padding: "12px 24px 24px",
    borderRadius: "24px",
    backgroundColor: "#FFFFFF",
    "& .MuiTextField-root": {
      margin: 0,
    },
    "& .MuiAlert-icon": {
      padding: 0,
      backgroundColor: "#67A7D5",
      borderTopLeftRadius: "10px",
      borderBottomLeftRadius: "10px",
      marginLeft: "-1px",
      height: "40px",
      alignItems: "center",
      "& .MuiSvgIcon-root": {
        height: "100%",
        color: "#FFFFFF",
      },
    },
    "& .MuiAlert-message": {
      padding: "5px",
      color: "#A2A2A2",
    },
    "& .MuiBox-root": {
      whiteSpace: "normal",
    },
  },
}));

const Detailed_Columns = [
  { id: "Timestamp", label: "Timestamp", width: "80px", align: "left", type: "date" },
  { id: "Message", label: "Message", width: "320px", align: "left" },
];

const formatDate = (date, displayFormat) =>
  displayFormat ? date.format("MM/DD/YYYY HH:mm:ss") : date.format("YYYY-MM-DD HH:mm:ss");

const HistoryColumns = [
  { id: "StartTime", label: "Start Time", width: "100px", align: "left", type: "date" },
  { id: "EndTime", label: "End Time", width: "100px", align: "left", type: "date" },
  { id: "LogName", label: "Log Name", width: "150px", align: "left" },
];

AppHistoryPopup.propTypes = {
  handleCancel: PropTypes.func,
  popupTitle: PropTypes.string,
  appId: PropTypes.string,
};

export default function AppHistoryPopup({ handleCancel, popupTitle, appId }) {
  const { Workspace_id } = useParams();

  const workspace = useSelector((state) => state.workspace);
  const { IsDataVaultEnabled } = workspace.workspace;

  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [needDetails, setNeedDetails] = useState(false);
  const [detailedHistory, setDetailedHistory] = useState([]);

  const [displayDate, setDisplayDate] = useState(null);

  //variables useful for pagination and sorting
  const [count, setCount] = useState(0);
  const [detailCount, setDetailCount] = useState(0);
  const [options, setOptions] = useState({
    orderBy: "StartTime",
    order: "desc",
  });
  const [detailOptions, setDetailOptions] = useState({
    orderBy: "Timestamp",
    order: "desc",
  });

  const optionsRef = useRef(options);
  const { classes } = useStyles();

  const handleBackClick = () => {
    setNeedDetails(false);
  };

  // Get History List
  const getHistoryList = useCallback(
    async (tableOptions = optionsRef.current) => {
      setLoading(true);

      const appService = new AppService();
      const result = await appService.getHistory(Workspace_id, appId, tableOptions);

      if (result.success) {
        const { historyData } = result.data;
        let historyDataArr = [];

        historyData.length &&
          historyData.forEach((row, key) => {
            historyDataArr.push([
              {
                logGroup_id: row.LogGroup_id,
                log_id: row.Log_id,
                logName: row.LogName,
                startTime: row.StartTime,
                endTime: row.EndTime,
                value: row.StartTime ? convertTimezone(row.StartTime) : "-",
                align: "left",
              },
              {
                logGroup_id: row.LogGroup_id,
                log_id: row.Log_id,
                logName: row.LogName,
                startTime: row.StartTime,
                endTime: row.EndTime,
                value: row.EndTime ? convertTimezone(row.EndTime) : "-",
                align: "left",
              },
              {
                logGroup_id: row.LogGroup_id,
                log_id: row.Log_id,
                logName: row.LogName,
                startTime: row.StartTime,
                endTime: row.EndTime,
                value: row.LogName ?? "-",
                align: "left",
              },
            ]);
          });
        setHistoryList(historyDataArr);
      }
      setDataLoaded(true);
      setLoading(false);
    },
    [Workspace_id, appId]
  );

  // Get Detailed History List
  const getDetailedHistoryList = useCallback(
    async (record) => {
      setLoading(true);

      const appService = new AppService();
      const result = await appService.getDetailedHistory(Workspace_id, appId, {
        LogGroup_id: record.logGroup_id,
        LogName: record.logName,
      });

      if (result.success) {
        const { historyData, count } = result.data;
        let historyDataArr = [];

        historyData.forEach((row) => {
          historyDataArr.push([
            {
              value: row.Time ? convertTimezone(row.Time) : "-",
              align: "left",
            },

            { value: row.Message, align: "left" },
          ]);
        });
        setDetailedHistory(historyDataArr);
        setDetailCount(count);
      } else setCount(0);

      setDataLoaded(true);
      setLoading(false);
    },
    [Workspace_id, appId]
  );

  const getDetailedHistory = (row) => {
    let StartTime = row[0].value !== "-" ? new Date(row[0].value) : null;
    let EndTime = row[1].value !== "-" ? new Date(row[1].value) : null;
    setDisplayDate([dayjs(StartTime), dayjs(EndTime)]);

    getDetailedHistoryList(row[0]);
    setNeedDetails(true);
  };

  useEffect(() => {
    getHistoryList();
    return () => {
      setLoading(false);
      setHistoryList([]);
      setDataLoaded(false);

      setCount(0);
    };
  }, [getHistoryList]);

  const title = useMemo(() => {
    return `${needDetails ? "Detailed " : ""}${popupTitle}`;
  }, [needDetails, popupTitle]);

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading && !dataLoaded}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <KMKDialog className={classes.dialogPaper} open={dataLoaded} onClose={handleCancel}>
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <DialogTitle sx={{ display: "flex", padding: "16px", marginBottom: "10px" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {needDetails ? (
              <ArrowBackIcon onClick={handleBackClick} sx={{ marginRight: "10px", cursor: "pointer" }} />
            ) : (
              <HistoryIcon sx={{ marginRight: "10px" }} />
            )}
            <span>
              {title}
              {needDetails &&
                displayDate &&
                !IsDataVaultEnabled &&
                `: "${formatDate(displayDate[0], true)}" - "${
                  displayDate[1] === null ? "Current" : formatDate(displayDate[1], true)
                }"`}
            </span>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              color: "black",
              marginLeft: "auto",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.contentContainer}>
          <Box>
            {!needDetails && (
              <TableWithPaginationSorting
                columns={HistoryColumns}
                tableData={historyList}
                ellipsisColumns={[3]}
                tableMaxWidth="100%"
                tableMaxHeight="400px"
                noDataColspan={4}
                noDataMessage="No records found"
                defaultOrder="desc"
                defaultOrderBy="StartTime"
                options={options}
                setOptions={setOptions}
                count={count}
                RowClick={getDetailedHistory}
                rowHover={true}
                usePagination={false}
                showCopy={false}
              />
            )}
            {needDetails && (
              <TableWithPaginationSorting
                columns={Detailed_Columns}
                tableData={detailedHistory}
                ellipsisColumns={[2]}
                tableMaxWidth="100%"
                tableMaxHeight="400px"
                noDataColspan={4}
                noDataMessage="No records found"
                defaultOrder="desc"
                defaultOrderBy="Timestamp"
                options={detailOptions}
                setOptions={setDetailOptions}
                count={detailCount}
                usePagination={false}
              />
            )}
          </Box>
        </DialogContent>
      </KMKDialog>
    </>
  );
}
